export interface InternalLink {
  label: string;
  path: string;
  /** Keywords that help match this link to blog post categories */
  keywords: string[];
}

export interface LinkCategory {
  heading: string;
  links: InternalLink[];
}

export const internalLinkCategories: LinkCategory[] = [
  {
    heading: 'By Account Size',
    links: [
      { label: 'Best 25K Funded Accounts', path: '/best/25k-funded-account', keywords: ['funded accounts', 'account size', 'evaluations'] },
      { label: 'Best 50K Funded Accounts', path: '/best/50k-funded-account', keywords: ['funded accounts', 'account size', 'evaluations'] },
      { label: 'Best 100K Funded Accounts', path: '/best/100k-funded-account', keywords: ['funded accounts', 'account size', 'evaluations'] },
      { label: 'Best 150K Funded Accounts', path: '/best/150k-funded-account', keywords: ['funded accounts', 'account size', 'evaluations'] },
      { label: 'Best 200K Funded Accounts', path: '/best/200k-funded-account', keywords: ['funded accounts', 'account size', 'evaluations'] },
    ],
  },
  {
    heading: 'By Drawdown Type',
    links: [
      { label: 'EOD Drawdown Firms', path: '/best/eod-drawdown-prop-firms', keywords: ['drawdown', 'risk management', 'prop firms'] },
      { label: 'Trailing Drawdown Firms', path: '/best/trailing-drawdown-prop-firms', keywords: ['drawdown', 'risk management', 'prop firms'] },
      { label: 'Static Drawdown Firms', path: '/best/static-drawdown-prop-firms', keywords: ['drawdown', 'risk management', 'prop firms'] },
    ],
  },
  {
    heading: 'By Feature',
    links: [
      { label: 'Cheapest Prop Firms', path: '/best/cheapest-prop-firms', keywords: ['prop firms', 'evaluations', 'pricing'] },
      { label: 'Highest Profit Split', path: '/best/highest-profit-split-prop-firms', keywords: ['profit splits', 'payouts', 'prop firms'] },
      { label: 'No Activation Fee', path: '/best/no-activation-fee-prop-firms', keywords: ['prop firms', 'evaluations', 'pricing'] },
      { label: 'No Consistency Rule', path: '/best/no-consistency-rule-prop-firms', keywords: ['prop firms', 'evaluations', 'trading strategies'] },
      { label: 'Instant Funding Firms', path: '/best/instant-funding-prop-firms', keywords: ['instant funding', 'prop firms', 'fast funding', 'evaluations'] },
      { label: 'Instant Funding 25K', path: '/best/instant-funding-25k-account', keywords: ['instant funding', 'funded accounts', 'account size'] },
      { label: 'Instant Funding 50K', path: '/best/instant-funding-50k-account', keywords: ['instant funding', 'funded accounts', 'account size'] },
      { label: 'Instant Funding 100K', path: '/best/instant-funding-100k-account', keywords: ['instant funding', 'funded accounts', 'account size'] },
      { label: 'Instant Funding 150K', path: '/best/instant-funding-150k-account', keywords: ['instant funding', 'funded accounts', 'account size'] },
      { label: 'Instant Funding 200K', path: '/best/instant-funding-200k-account', keywords: ['instant funding', 'funded accounts', 'account size'] },
    ],
  },
  {
    heading: 'By Trading Style',
    links: [
      { label: 'Best for Beginners', path: '/category/beginners', keywords: ['beginners', 'getting started', 'prop firms'] },
      { label: 'Best for Scalpers', path: '/category/scalpers', keywords: ['scalping', 'trading strategies', 'day trading'] },
      { label: 'Best for Swing Traders', path: '/category/swing-traders', keywords: ['swing trading', 'trading strategies'] },
    ],
  },
  {
    heading: 'Tools & Guides',
    links: [
      { label: 'Compare All Firms', path: '/compare', keywords: ['prop firms', 'compare', 'evaluations'] },
      { label: 'Drawdown Simulator', path: '/tools/drawdown-simulator', keywords: ['drawdown', 'tools', 'risk management'] },
      { label: 'Trading Guides & Blog', path: '/blog', keywords: ['guides', 'education', 'futures trading'] },
      { label: 'Our Methodology', path: '/methodology', keywords: ['methodology', 'reviews'] },
    ],
  },
];

/**
 * Returns link categories relevant to the given blog post categories.
 * Always returns at least 3 categories so the grid looks full.
 */
export function getRelevantLinkCategories(
  postCategories: string[],
  currentPath?: string,
): LinkCategory[] {
  if (!postCategories.length) return internalLinkCategories;

  const lower = postCategories.map(c => c.toLowerCase());

  // Score each category by how many keyword matches it has
  const scored = internalLinkCategories.map(cat => {
    const score = cat.links.reduce((sum, link) => {
      const matches = link.keywords.filter(kw =>
        lower.some(pc => pc.includes(kw) || kw.includes(pc)),
      ).length;
      return sum + matches;
    }, 0);
    return { cat, score };
  });

  // Sort by relevance, keep all (always show the full set for max link juice)
  scored.sort((a, b) => b.score - a.score);

  const result = scored.map(s => s.cat);

  // Filter out the current page from links if provided
  if (currentPath) {
    return result.map(cat => ({
      ...cat,
      links: cat.links.filter(l => l.path !== currentPath),
    })).filter(cat => cat.links.length > 0);
  }

  return result;
}
